import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, Paperclip } from "lucide-react";

export default function BookingEmailDialog({ quoteId, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState("");
  const [hasBolPdf, setHasBolPdf] = useState(false);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/quotes/${quoteId}/booking-confirmation-draft`)
      .then((r) => {
        setTo(r.data.to || "");
        setSubject(r.data.subject || "");
        setMessage(r.data.message || "");
        setQuoteNumber(r.data.quote_number || "");
        setHasBolPdf(!!r.data.has_bol_pdf);
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [open, quoteId]);

  const send = async () => {
    if (!to.trim()) {
      toast.error("Enter the customer's email address.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/booking-confirmation-email`, { to: to.trim(), subject, message });
      toast.success(`Booking confirmation sent to ${to.trim()}${data.attached_bol ? " (BOL PDF attached)" : ""}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="booking-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-[hsl(var(--success))]" /> Booking confirmation {quoteNumber && `· ${quoteNumber}`}
          </DialogTitle>
          <DialogDescription>Pre-drafted confirmation with the shipment details — sent from your connected Gmail.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="booking-email-to">To</Label>
              <Input id="booking-email-to" type="email" value={to} onChange={(e) => setTo(e.target.value)}
                placeholder="customer@company.com" data-testid="booking-email-to-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="booking-email-subject">Subject</Label>
              <Input id="booking-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="booking-email-subject-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="booking-email-message">Message</Label>
              <Textarea id="booking-email-message" value={message} onChange={(e) => setMessage(e.target.value)}
                rows={10} className="text-sm" data-testid="booking-email-message-input" />
            </div>
            {hasBolPdf && (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Paperclip className="h-3.5 w-3.5" /> The R+L BOL PDF will be attached automatically.
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="booking-email-cancel">Cancel</Button>
          <Button onClick={send} disabled={sending || loading} data-testid="booking-email-send"
            className="bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success))]/90">
            {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />}Send confirmation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
